import { z } from 'zod';
import { PLAIN_NAME_MESSAGE, plainName } from './json.js';
import { ROLES } from './roles.js';
/**
 * Roles and permissions (task 78, decision 0055). What a person may do is a set of permissions: one per
 * area and action (view, create, edit, delete, and other actions such as approve or export). The eight
 * built-in roles are fixed sets of them, exactly what each role could do before; a tenant's administrators
 * can add their own roles as any mix of the permissions their kind of organisation has. The API's routes,
 * the database's row policies and the web app all check permissions, never role names.
 */
export const CRUD = ['view', 'create', 'edit', 'delete'];
const [TA, CE, CA, UW, BR, SO, SM, AU] = ROLES;
const ALL8 = ROLES;
/** Insurer and broker staff, and the people answering for a business: everyone who works on a check. */
const WORKERS = [TA, UW, BR, SO, SM];
const SEE_DETAIL = [...WORKERS, AU];
const CLIENT_SIDE = [TA, UW, BR, AU, CE, CA];
const STAFF = [TA, UW];
const STAFF_AU = [TA, UW, AU];
const BOOK = [TA, UW, BR, AU];
const CONFIG_READ = [TA, CE, CA, AU];
export const PERMISSION_AREAS = [
    { key: 'assessments', label: 'Assessments', group: 'Cyber Checks', hint: 'Cyber Checks, their questions, answers and scores' },
    { key: 'evidence', label: 'Documents and activity', group: 'Cyber Checks', hint: 'Documents, suggestions, obligations, tasks and change requests on a check' },
    { key: 'messages', label: 'Messages', group: 'Cyber Checks', hint: 'The thread on each check, and the insurer’s internal notes' },
    { key: 'reviews', label: 'Reviews', group: 'Cyber Checks', hint: 'Reviewing answers, asking for changes, claim checks and staff corrections' },
    { key: 'decisions', label: 'Decisions', group: 'Cyber Checks', hint: 'Cover decisions and their conditions' },
    { key: 'workflows', label: 'Workflows', group: 'Cyber Checks', hint: 'Starting a workflow on a check' },
    { key: 'conflicts', label: 'Conflicts', group: 'Cyber Checks', hint: 'Answers that disagree with what was found elsewhere' },
    { key: 'rescores', label: 'Re-scores', group: 'Cyber Checks', hint: 'Scoring checks again on a newer configuration' },
    { key: 'connections', label: 'Connections', group: 'Cyber Checks', hint: 'Microsoft 365 and other connected systems' },
    { key: 'outside_in', label: 'Internet check', group: 'Cyber Checks', hint: 'What a business’s domains show from the internet, and its consent' },
    { key: 'sharing', label: 'Score badges and passports', group: 'Cyber Checks', hint: 'What a business shares of its score' },
    { key: 'assistant', label: 'AI assistant', group: 'Cyber Checks', hint: 'Questions about the book and underwriting memos' },
    { key: 'clients', label: 'Clients', group: 'Book of business', hint: 'Client records, saved views, import and export' },
    { key: 'client_fields', label: 'Client fields', group: 'Book of business', hint: 'The organisation’s own fields on client records' },
    { key: 'portfolio', label: 'Portfolio', group: 'Book of business', hint: 'Scores across the book' },
    { key: 'suppliers', label: 'Suppliers', group: 'Book of business', hint: 'Suppliers the book shares' },
    { key: 'intake', label: 'Submissions', group: 'Book of business', hint: 'Submission links and what comes through them' },
    { key: 'campaigns', label: 'Renewals', group: 'Book of business', hint: 'Renewal campaigns' },
    { key: 'alerts', label: 'Alerts', group: 'Book of business', hint: 'The alerts inbox' },
    { key: 'alert_rules', label: 'Alert rules', group: 'Book of business', hint: 'What raises an alert, and for whom' },
    { key: 'automations', label: 'Automations', group: 'Book of business', hint: 'Routing and reminders that run on their own' },
    { key: 'people', label: 'People', group: 'Organisation', hint: 'Members, invitations and the roles people hold' },
    { key: 'roles', label: 'Roles', group: 'Organisation', hint: 'The organisation’s own roles and what they may do' },
    { key: 'organisation', label: 'Organisation', group: 'Organisation', hint: 'The organisation’s name' },
    { key: 'notices', label: 'Email notices', group: 'Organisation', hint: 'The wording of notice emails' },
    { key: 'api_keys', label: 'API keys', group: 'Organisation', hint: 'Keys for the read API' },
    { key: 'webhooks', label: 'Webhooks', group: 'Organisation', hint: 'Where events are sent, and their deliveries' },
    { key: 'api', label: 'Read API', group: 'Organisation', hint: 'The /v1 API' },
    { key: 'evidence_pack', label: 'Evidence pack', group: 'Organisation', hint: 'The RMiT evidence pack and the audit trail' },
    { key: 'ai', label: 'AI usage', group: 'Organisation', hint: 'What AI features have used' },
    { key: 'ai_providers', label: 'AI providers', group: 'Configuration', hint: 'The AI models the platform uses' },
    { key: 'config', label: 'Configuration', group: 'Configuration', hint: 'Questions, rules and journeys in the studio' },
    { key: 'releases', label: 'Releases', group: 'Configuration', hint: 'Publishing configuration, with a second person’s approval' },
    { key: 'organisations', label: 'Organisations', group: 'Configuration', hint: 'Insurers and brokers on the platform' },
]; // prettier-ignore
const P = (area, action, label, roles, key, widens) => ({ key: `${area}.${key ?? action}`, area, action, label, roles, ...(widens ? { widens: `${area}.${widens}` } : {}) }); // prettier-ignore
export const PERMISSIONS = [
    P('assessments', 'view', 'See checks they started or take part in', ALL8),
    P('assessments', 'view', 'See every check', [TA, UW, AU, CE, CA], 'view_all', 'view'),
    P('assessments', 'create', 'Start checks and add people to them', [TA, UW, BR]),
    P('assessments', 'edit', 'Answer questions', WORKERS),
    P('assessments', 'other', 'Submit or finish a check', [TA, UW, BR, SO], 'submit'),
    P('assessments', 'other', 'Start the business’s own check', [TA, SO], 'start_own'),
    P('evidence', 'view', 'See documents and activity', SEE_DETAIL),
    P('evidence', 'create', 'Upload documents and evidence', WORKERS),
    P('messages', 'view', 'Read the thread', SEE_DETAIL),
    P('messages', 'create', 'Write in the thread', WORKERS),
    P('messages', 'other', 'Read internal notes', STAFF_AU, 'internal_view'),
    P('messages', 'other', 'Write internal notes', STAFF, 'internal'),
    P('reviews', 'view', 'See reviews and rule results', STAFF_AU),
    P('reviews', 'edit', 'Review answers, ask for changes, run claim checks, correct answers', STAFF),
    P('decisions', 'view', 'See decisions and conditions', SEE_DETAIL),
    P('decisions', 'create', 'Decide on cover', STAFF),
    P('decisions', 'edit', 'Clear, send back or waive conditions', STAFF),
    P('workflows', 'create', 'Start workflows', STAFF),
    P('conflicts', 'view', 'See every conflict', STAFF_AU),
    P('conflicts', 'other', 'Confirm or dismiss conflicts', STAFF, 'decide'),
    P('conflicts', 'other', 'Dispute a conflict', [SO, SM, BR], 'dispute'),
    P('rescores', 'view', 'See re-scores', STAFF_AU),
    P('rescores', 'create', 'Propose and withdraw re-scores', STAFF),
    P('rescores', 'other', 'Approve or reject re-scores', STAFF, 'approve'),
    P('connections', 'create', 'Connect and sync systems', [TA, UW, BR, SO]),
    P('connections', 'delete', 'Disconnect systems', [TA, UW, BR, SO]),
    P('connections', 'other', 'Accept readings as answers', STAFF, 'accept'),
    P('outside_in', 'view', 'See the internet check', STAFF_AU),
    P('outside_in', 'other', 'Run checks and accept readings', STAFF, 'check'),
    P('outside_in', 'other', 'See the business’s consent', [TA, UW, AU, SO, SM], 'consent_view'),
    P('outside_in', 'other', 'Give or withdraw consent', [SO], 'consent'),
    P('sharing', 'view', 'See shared badges and passports', SEE_DETAIL),
    P('sharing', 'create', 'Share a badge or passport', [SO]),
    P('sharing', 'delete', 'Stop sharing', [SO, TA]),
    P('assistant', 'other', 'Ask about the book', BOOK, 'book'),
    P('assistant', 'other', 'Draft underwriting memos', STAFF, 'memo'),
    P('clients', 'view', 'See their own clients', CLIENT_SIDE),
    P('clients', 'view', 'See every client', [TA, UW, AU, CE, CA], 'view_all', 'view'),
    P('clients', 'create', 'Add clients', [TA, UW, BR]),
    P('clients', 'edit', 'Edit their own clients', [TA, UW, BR]),
    P('clients', 'edit', 'Edit every client', STAFF, 'edit_all', 'edit'),
    P('clients', 'other', 'Import clients', [TA, UW, BR], 'import'),
    P('clients', 'other', 'Export clients', CLIENT_SIDE, 'export'),
    P('client_fields', 'create', 'Add fields', [TA]),
    P('client_fields', 'edit', 'Change and order fields', [TA]),
    P('client_fields', 'delete', 'Remove fields', [TA]),
    P('portfolio', 'view', 'See the portfolio', CLIENT_SIDE),
    P('portfolio', 'other', 'See loss estimates', BOOK, 'losses'),
    P('suppliers', 'view', 'See shared suppliers', BOOK),
    P('intake', 'view', 'See their own links and submissions', BOOK),
    P('intake', 'view', 'See every link and submission', STAFF_AU, 'view_all', 'view'),
    P('intake', 'create', 'Create submission links', [TA, UW, BR]),
    P('intake', 'edit', 'Change their own links', [TA, UW, BR]),
    P('intake', 'edit', 'Change every link', STAFF, 'edit_all', 'edit'),
    P('intake', 'other', 'Accept or reject submissions', [TA, UW, BR], 'decide'),
    P('campaigns', 'view', 'See renewal campaigns', BOOK),
    P('campaigns', 'view', 'See every client in a campaign', STAFF_AU, 'view_all', 'view'),
    P('campaigns', 'create', 'Start campaigns', STAFF),
    P('campaigns', 'edit', 'Add clients to and close campaigns', STAFF),
    P('alerts', 'view', 'See alerts sent to them', BOOK),
    P('alerts', 'view', 'See every alert', STAFF_AU, 'view_all', 'view'),
    P('alert_rules', 'view', 'See alert rules', STAFF_AU),
    P('alert_rules', 'create', 'Add alert rules', STAFF),
    P('alert_rules', 'edit', 'Change alert rules', STAFF),
    P('automations', 'view', 'See automations', STAFF_AU),
    P('automations', 'edit', 'Set up automations', STAFF),
    P('people', 'view', 'See members and invitations', [TA]),
    P('people', 'create', 'Invite people', [TA]),
    P('people', 'edit', 'Change roles and suspend people', [TA]),
    P('people', 'delete', 'Revoke invitations', [TA]),
    P('roles', 'view', 'See roles', [TA]),
    P('roles', 'create', 'Add roles', [TA]),
    P('roles', 'edit', 'Change roles', [TA]),
    P('roles', 'delete', 'Remove roles', [TA]),
    P('organisation', 'edit', 'Rename the organisation', [TA]),
    P('notices', 'view', 'See notice wording', [TA]),
    P('notices', 'edit', 'Change notice wording', [TA]),
    P('notices', 'delete', 'Go back to the standard wording', [TA]),
    P('api_keys', 'view', 'See API keys', [TA]),
    P('api_keys', 'create', 'Create API keys', [TA]),
    P('api_keys', 'delete', 'Revoke API keys', [TA]),
    P('webhooks', 'view', 'See webhooks and deliveries', [TA]),
    P('webhooks', 'create', 'Add webhooks', [TA]),
    P('webhooks', 'edit', 'Change, test and retry webhooks', [TA]),
    P('api', 'view', 'Read through the API', STAFF_AU),
    P('evidence_pack', 'view', 'See the evidence pack', [TA, AU]),
    P('evidence_pack', 'other', 'Download the audit trail', [TA, AU], 'export'),
    P('ai', 'view', 'See AI usage', [TA]),
    P('ai_providers', 'view', 'See AI providers', [TA, AU]),
    P('ai_providers', 'create', 'Add and try AI providers', [TA]),
    P('ai_providers', 'edit', 'Change AI providers', [TA]),
    P('ai_providers', 'delete', 'Remove AI providers', [TA]),
    P('config', 'view', 'See configuration and releases', CONFIG_READ),
    P('config', 'create', 'Draft configuration', [CE]),
    P('config', 'edit', 'Change drafts', [CE]),
    P('config', 'delete', 'Delete drafts', [CE]),
    P('config', 'other', 'Read the configuration checks run on', [...CONFIG_READ, UW], 'bundle'),
    P('releases', 'create', 'Ask for releases and rollbacks', [CE]),
    P('releases', 'delete', 'Withdraw releases', [CE]),
    P('releases', 'other', 'Approve or reject releases', [CA], 'approve'),
    P('organisations', 'view', 'See insurers and brokers', [TA]),
    P('organisations', 'create', 'Set up insurers and brokers', [TA]),
]; // prettier-ignore
export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key);
const BY_KEY = new Map(PERMISSIONS.map((p) => [p.key, p]));
export const permissionDef = (key) => BY_KEY.get(key);
export const isPermission = (key) => BY_KEY.has(key);
/** What each built-in role may do. */
export const BUILT_IN_GRANTS = Object.fromEntries(ROLES.map((r) => [r, PERMISSIONS.filter((p) => p.roles.includes(r)).map((p) => p.key)]));
export const isBuiltInRole = (key) => ROLES.includes(key);
/** Custom role keys: "c_" and twelve characters, made when the role is created; the name can change. */
export const CUSTOM_ROLE_KEY = /^c_[a-z0-9]{12}$/;
/**
 * Everything someone holding these roles may do. Unknown keys (a custom role since removed) grant nothing.
 * A wider permission ("every client") brings the narrower one with it.
 */
export function permissionsOf(roles, custom = []) {
    const out = new Set();
    for (const r of roles) {
        const grants = isBuiltInRole(r) ? BUILT_IN_GRANTS[r] : (custom.find((c) => c.key === r)?.permissions ?? []); // prettier-ignore
        for (const p of grants)
            if (BY_KEY.has(p))
                out.add(p);
    }
    for (const p of [...out]) {
        const narrower = BY_KEY.get(p).widens;
        if (narrower)
            out.add(narrower);
    }
    return out;
}
export const can = (perms, p) => perms instanceof Set ? perms.has(p) : perms.includes(p);
export const canAny = (perms, ...ps) => ps.some((p) => can(perms, p));
/** Permissions a business's own people hold; anything beyond them is staff work, which needs two-step sign-in. */
export const BUSINESS_PERMISSIONS = permissionsOf([SO, SM]);
export const isBusinessOnly = (perms) => [...perms].every((p) => BUSINESS_PERMISSIONS.has(p)); // prettier-ignore
/** Built-in roles each kind of organisation hands out. The platform configures the product; it has no assessments. */
export const KIND_ROLES = {
    insurer: ROLES,
    broker: [TA, BR, SO, SM, AU],
    platform: [TA, CE, CA, AU],
    // A business on its own (decision 0034): its owners and the staff who help answer.
    business: [TA, SO, SM],
};
/** The parts of the product each kind of organisation has: the platform configures it and has no checks or book. */
const KIND_GROUPS = {
    insurer: ['Cyber Checks', 'Book of business', 'Organisation'],
    broker: ['Cyber Checks', 'Book of business', 'Organisation'],
    platform: ['Organisation', 'Configuration'],
    business: ['Cyber Checks', 'Organisation'],
};
/**
 * Permissions a role may have in this kind of organisation: what its built-in roles cover between them, in
 * the parts of the product it has.
 */
export function kindPermissions(kind) {
    const groups = KIND_GROUPS[kind] ?? [];
    const group = (k) => PERMISSION_AREAS.find((a) => a.key === BY_KEY.get(k).area).group;
    // A business's own checks are answered, not reviewed: its roles mix what its owners and members do, and
    // its administrators' running of the organisation.
    const held = kind === 'business'
        ? new Set([
            ...BUSINESS_PERMISSIONS,
            ...[...permissionsOf([TA])].filter((k) => group(k) === 'Organisation'),
        ])
        : permissionsOf(KIND_ROLES[kind] ?? []);
    return PERMISSION_KEYS.filter((k) => held.has(k) && groups.includes(group(k)));
}
/** Areas, in matrix order, that have any of these permissions. */
export const areasFor = (keys) => {
    const set = new Set(keys);
    return PERMISSION_AREAS.filter((a) => PERMISSIONS.some((p) => p.area === a.key && set.has(p.key)));
};
const Permissions = z
    .array(z.string().refine(isPermission, 'Not a permission'))
    .max(PERMISSIONS.length)
    // A wider permission ("every client") brings the narrower one with it; kept in matrix order.
    .transform((ps) => {
    const set = new Set(ps.flatMap((p) => [p, ...(BY_KEY.get(p)?.widens ? [BY_KEY.get(p).widens] : [])]));
    return PERMISSION_KEYS.filter((k) => set.has(k));
});
export const RoleInput = z.strictObject({
    name: z.string().trim().min(2, 'Give the role a name').max(60, 'Use at most 60 characters').refine(plainName, PLAIN_NAME_MESSAGE),
    description: z.string().trim().max(300, 'Use at most 300 characters').refine(plainName, PLAIN_NAME_MESSAGE).nullable().default(null),
    permissions: Permissions.refine((p) => p.length > 0, 'Give the role at least one permission'),
}); // prettier-ignore
export const RolePatch = RoleInput.partial().refine((r) => Object.keys(r).length > 0, 'Change something'); // prettier-ignore
/** Permissions a role would give that the person granting it doesn't hold. */
export const beyond = (granting, mine) => [...new Set(granting)].filter((p) => !mine.has(p));
/** The label of a role key, built-in or custom. */
export function roleName(key, custom, labels) {
    return labels[key] ?? custom.find((c) => c.key === key)?.name ?? 'Removed role';
}
//# sourceMappingURL=rbac.js.map